import { useMemo } from "react";
import { Avatar, Box, ButtonBase, Dialog, IconButton, Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { avatarColor, compactCount, getAuthorLabel, getAvatarUrl, getMediaInfo, initials } from "./communityUtils";

function roleLabel(user) {
  const role = String(user?.role || "").toLowerCase();
  if (!role) return "";
  if (role === "custom_user" || role === "professor") return "Educator";
  return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, " ");
}

/** Profile card for a post's author, built from posts already loaded in the feed. */
function ProfileDialog({ item, items, onClose, onOpenPost }) {
  const user = item?.user;
  const name = getAuthorLabel(item);
  const color = avatarColor(name);
  const avatarUrl = getAvatarUrl(item);
  const role = roleLabel(user);
  const university = user?.university?.name || user?.University?.name || "";

  const posts = useMemo(() => {
    if (!user?.id) return item ? [item] : [];
    return items.filter((p) => String(p?.user?.id) === String(user.id));
  }, [items, item, user?.id]);

  const stats = useMemo(
    () => [
      { label: "Posts", value: posts.length },
      { label: "Comments", value: posts.reduce((n, p) => n + (Number(p?.commentCount) || 0), 0) },
      { label: "Views", value: posts.reduce((n, p) => n + (Number(p?.viewCount) || 0), 0) },
    ],
    [posts]
  );

  const mediaPosts = posts.filter((p) => getMediaInfo(p).url).slice(0, 6);

  return (
    <Dialog
      open={Boolean(item)}
      onClose={onClose}
      maxWidth={false}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(2, 6, 23, 0.5)", backdropFilter: "blur(8px)" } } }}
      PaperProps={{
        sx: {
          width: "min(440px, calc(100vw - 32px))",
          m: 2,
          borderRadius: 5,
          overflow: "hidden",
          boxShadow: "0 40px 120px rgba(2, 6, 23, 0.35)",
        },
      }}
    >
      {/* Cover */}
      <Box
        sx={{
          position: "relative",
          height: 132,
          background: `linear-gradient(135deg, ${color} 0%, ${color}cc 45%, #0f172a 130%)`,
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle at 18% 30%, rgba(255,255,255,0.28) 0, transparent 38%), radial-gradient(circle at 85% 0%, rgba(255,255,255,0.18) 0, transparent 40%)",
          },
        }}
      >
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 1,
            color: "#fff",
            bgcolor: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(8px)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Identity */}
      <Stack alignItems="center" sx={{ px: 3, mt: "-56px", position: "relative" }}>
        <Avatar
          src={avatarUrl || undefined}
          alt={name}
          sx={{
            width: 112,
            height: 112,
            fontSize: 38,
            fontWeight: 800,
            color: "#fff",
            bgcolor: color,
            border: "5px solid #fff",
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.18)",
          }}
        >
          {initials(name)}
        </Avatar>
        <Typography sx={{ mt: 1.5, fontSize: 21, fontWeight: 800, color: "ink.strong", letterSpacing: "-0.02em", textAlign: "center" }}>
          {name}
        </Typography>
        {role || university ? (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75, flexWrap: "wrap", justifyContent: "center" }}>
            {role ? (
              <Box sx={{ px: 1.1, py: 0.35, borderRadius: 999, fontSize: 12, fontWeight: 700, color, bgcolor: `${color}14` }}>
                {role}
              </Box>
            ) : null}
            {university ? <Typography sx={{ fontSize: 13, color: "#64748b" }}>{university}</Typography> : null}
          </Stack>
        ) : null}
      </Stack>

      {/* Stats */}
      <Stack
        direction="row"
        sx={{ mx: 3, mt: 2.5, borderRadius: 3, bgcolor: "background.elevated", border: "1px solid rgba(15, 23, 42, 0.05)" }}
      >
        {stats.map((s, i) => (
          <Box
            key={s.label}
            sx={{ flex: 1, py: 1.5, textAlign: "center", borderLeft: i ? "1px solid rgba(15, 23, 42, 0.06)" : 0 }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "ink.strong", lineHeight: 1.2 }}>
              {compactCount(s.value)}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{s.label}</Typography>
          </Box>
        ))}
      </Stack>

      {/* Recent media */}
      <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
        {mediaPosts.length ? (
          <>
            <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8", mb: 1.25 }}>
              Recent posts
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
              {mediaPosts.map((p) => {
                const media = getMediaInfo(p);
                const thumb = media.isVideo ? media.posterUrl : media.url;
                return (
                  <ButtonBase
                    key={p.id}
                    onClick={() => onOpenPost(p)}
                    sx={{
                      position: "relative",
                      aspectRatio: "1 / 1",
                      borderRadius: 2.5,
                      overflow: "hidden",
                      bgcolor: "#0f172a",
                      backgroundImage: thumb ? `url(${thumb})` : "none",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transition: "transform 160ms ease",
                      "&:hover": { transform: "scale(1.03)" },
                    }}
                  >
                    {media.isVideo ? <PlayArrowRoundedIcon sx={{ color: "#fff", fontSize: 28 }} /> : null}
                  </ButtonBase>
                );
              })}
            </Box>
          </>
        ) : (
          <Typography sx={{ fontSize: 13.5, color: "#94a3b8", textAlign: "center" }}>No photo posts yet.</Typography>
        )}
      </Box>
    </Dialog>
  );
}

ProfileDialog.propTypes = {
  item: PropTypes.object,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenPost: PropTypes.func.isRequired,
};

export default ProfileDialog;
